import { createFileRoute } from "@tanstack/react-router";
import type { MasterPlanAnalysis } from "api-types/common/responseTypes";
import { PLAN_ANALYSIS_S3_PATH, WEB_API_BASE_PATH } from "common/constants";
import { jsonToCSV } from "common/utils/csv";
import downloadFile from "common/utils/downloadFile";
import AdminPage from "lib/components/admin/builders/AdminPage";
import { fetcher, useFetchImmutable } from "lib/fetcher";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "ui/buttons/Button";
import DropdownMenu, { DropdownMenuButton } from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import cx from "ui/cx";
import Checkbox from "ui/forms/CheckboxOld";
import IconCheck from "ui/icons/IconCheck";
import IconDownload from "ui/icons/IconDownload";
import IconMiniChevronDown from "ui/icons/IconMiniChevronDown";
import DataTable from "ui/tables/DataTable";
import type { DataTableColumn, HeaderContext } from "ui/tables/DataTable/types";
import withAuthentication from "../../components/withAuthentication";
import styles from "./PlanAnalysis.module.scss";

type SummaryOperation = "Sum" | "Average" | "Median" | "Minimum" | "Maximum";

function calulateFooterStats(rows: number[]) {
  if (rows?.length === 0) {
    return {
      Sum: 0,
      Average: 0,
      Median: 0,
      Minimum: 0,
      Maximum: 0,
    };
  }

  rows.sort((a, b) => a - b);
  const sum = rows.reduce((sum, currentRow) => sum + currentRow, 0);

  return {
    Sum: sum,
    Average: (sum / rows.length).toFixed(2),
    Median: rows[Math.floor(rows.length / 2)],
    Minimum: rows[0],
    Maximum: rows[rows.length - 1],
  };
}

// Fetches and updates the analysis data.
const fetchAndSetMasterPlanAnalysis = async (baseFileStorageUrl: string) => {
  try {
    return await fetcher<MasterPlanAnalysis[]>(`${baseFileStorageUrl}${PLAN_ANALYSIS_S3_PATH}`, false, {
      "Cache-Control": "no-cache",
    });
  } catch (error) {
    console.log(`Failed to retrieve analysis: ${error}`);
  }
  return undefined;
};

// Analysis dashboard that displays a table of statistics (author, window count, facade count, etc.)
// for every master plan. The analysis data itself is stored in a file in s3 and is recomputed on request.
function AdminPlanAnalysisPage() {
  const { data: baseFileStorageUrl } = useFetchImmutable<string>(`${WEB_API_BASE_PATH}/files/file-storage-base-url/`);
  const [analysis, setAnalysis] = useState<MasterPlanAnalysis[]>([]);

  // Column selection functionality. Alters visible columns per selection.
  const [operationSelection, setOperationSelection]: [SummaryOperation, Dispatch<SetStateAction<SummaryOperation>>] =
    useState<SummaryOperation>("Maximum");

  const footer = useCallback(
    ({ table, column }: HeaderContext<MasterPlanAnalysis, unknown>) =>
      calulateFooterStats(table.getFilteredRowModel().rows.map((row) => row.getValue(column.id)))[operationSelection],
    [operationSelection],
  );

  const columns: DataTableColumn<MasterPlanAnalysis>[] = useMemo(
    () => [
      {
        field: "name",
        header: "Master Plan",
        footer: operationSelection,
      },
      { field: "builder", header: "Builder" },
      { field: "builderCategory", header: "Builder Category" },
      {
        field: "authors",
        header: "Authors",
        accessor: (row) => row.authors.map((author) => `${author.firstName} ${author.lastName}`).join(", "),
      },
      { field: "createdAt", header: "Created" },
      { field: "publishedAt", header: "Published" },
      {
        field: "roomCount",
        header: "Rooms",
        footer,
      },
      {
        field: "blockCount",
        header: "Blocks",
        footer,
      },
      {
        field: "facadeCount",
        header: "Facades",
        footer,
      },
      {
        field: "facadeConditionStatementCount",
        header: "Facade Conditions",
        footer,
      },
      {
        field: "roomElevationNoteCount",
        header: "Room Elevation Notes",
        footer,
      },
      {
        field: "annotationLeaderCount",
        header: "Annotations - Leader",
        footer,
      },
      {
        field: "annotationNoteCount",
        header: "Annotations - Note",
        footer,
      },
      {
        field: "annotationDimensionCount",
        header: "Annotations - Dimension",
        footer,
      },
      {
        field: "annotationDetailCount",
        header: "Annotations - Detail",
        footer,
      },
      {
        field: "annotationLineworkCount",
        header: "Annotations - Linework",
        footer,
      },
      {
        field: "caseworkPlanNoteCount",
        header: "Casework Plan Notes",
        footer,
      },
      {
        field: "lpDrawingTemplateCount",
        header: "LP Drawing Templates",
        footer,
      },
      {
        field: "lpDrawingTemplateInstanceCount",
        header: "LP Drawing Template Instances",
        footer,
      },
      {
        field: "lpSheetTemplateCount",
        header: "LP Sheet Templates",
        footer,
      },
      {
        field: "lpSheetTemplateInstanceCount",
        header: "LP Sheet Template Instances",
        footer,
      },
      {
        field: "planSetTemplateCount",
        header: "Plan Set Templates",
        footer,
      },
      {
        field: "planSetTemplateInstanceCount",
        header: "Plan Set Template Instances",
        footer,
      },
      {
        field: "windowCount",
        header: "Windows",
        footer,
      },
      {
        field: "subroofCount",
        header: "Subroofs",
        footer,
      },
      {
        field: "settingsSetCountNoRefs",
        header: "Settings Sets",
        footer,
      },
    ],
    [operationSelection, footer],
  );

  const defaultColumnSelection: ColumnSelection = {
    ...Object.fromEntries(Object.values(columns).map((v) => [v.field, false])),
    name: true,
    builder: true,
    blockCount: true,
    roomCount: true,
    facadeCount: true,
    settingsSetCountNoRefs: true,
    subroofCount: true,
  };

  const [columnSelections, setColumnSelections] = useState<ColumnSelection>(defaultColumnSelection);

  const selectedColumns = useMemo(() => {
    return columns.map((column) => {
      const hide = !columnSelections[column.field ?? ""];
      return { ...column, hide, enableGlobalFilter: !hide };
    });
  }, [columns, columnSelections]);

  useEffect(() => {
    if (baseFileStorageUrl) {
      void fetchAndSetMasterPlanAnalysis(baseFileStorageUrl).then((result) => setAnalysis(result ?? []));
    }
  }, [baseFileStorageUrl]);

  const updatedAt = analysis.length > 0 && (
    <div className={styles.updatedAt}>
      <b>Last updated:</b> {new Date(analysis[0].analysisDate).toString()}
    </div>
  );

  const downloadCSV = async () => {
    try {
      const csvFilename = `plan-analysis-raw-${new Date().toISOString()}.csv`;
      const csv = await jsonToCSV(analysis);

      downloadFile(csvFilename, csv, "text/csv");
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <AdminPage currentPage="plan-analysis" pageTitle="Admin - Plan Analysis">
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>Plan Analysis</span>
          <span className={styles.headerActions}>
            <Button btnSize="medium" btnStyle="secondary" onClick={downloadCSV}>
              <IconDownload size={15} className="button-icon" />
              Raw .CSV
            </Button>
          </span>
        </div>
        <hr className={styles.hr} />
        <div className={cx({ styles, gridControls: true })}>
          {updatedAt}
          <OperationSelectionDropdown
            operations={["Average", "Sum", "Minimum", "Maximum", "Median"]}
            currentOperation={operationSelection}
            setOperationSelection={setOperationSelection}
          />
          <ColumnSelectionDropdown
            columns={columns}
            columnSelections={columnSelections}
            setColumnSelections={setColumnSelections}
          />
        </div>
        <DataTable
          title="Plan Analysis"
          data={analysis}
          columns={selectedColumns}
          className={styles.dataTable}
          controls={{
            search: "Search",
            downloadCsv: { filename: `plan-analysis-filtered-${new Date().toISOString()}` },
          }}
          fixedHeader
          fixedFooter
          stickyPagination
        />
      </div>
    </AdminPage>
  );
}

// Used to track which columns are visible.
interface ColumnSelection {
  [key: string]: boolean;
}

interface ColumnSelectionDropdownProps {
  columns: DataTableColumn<MasterPlanAnalysis>[];
  columnSelections: ColumnSelection;
  setColumnSelections(value: ColumnSelection): void;
}

// Component for selecting which columns are visible.
function ColumnSelectionDropdown({ columns, columnSelections, setColumnSelections }: ColumnSelectionDropdownProps) {
  const [openMenu, setOpenMenu] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <DropdownMenuButton open={openMenu} handleClick={() => setOpenMenu(!openMenu)}>
        <Button btnSize="medium" btnStyle="primary">
          <IconMiniChevronDown size={12} />
          <span>Fields</span>
        </Button>
      </DropdownMenuButton>
      {openMenu && (
        <DropdownMenu onClose={() => setOpenMenu(false)}>
          {columns.map((column) => {
            if (column.field === "name") {
              return null;
            }
            return (
              <div key={column.field}>
                <Checkbox
                  id={`column_${column.field}`}
                  checked={column.field && columnSelections[column.field]}
                  onChange={(evt) =>
                    setColumnSelections({ ...columnSelections, [column.field ?? ""]: evt.target.checked })
                  }
                />
                <label htmlFor={`column_${column.field}`} className={styles.checkboxLabel}>
                  {column.header?.toString()}
                </label>
              </div>
            );
          })}
        </DropdownMenu>
      )}
    </div>
  );
}

interface OperationSelectionDropdownProps {
  operations: SummaryOperation[];
  currentOperation: SummaryOperation;
  setOperationSelection(operation: SummaryOperation): void;
}

// Component for selecting the summary operation (min, max, average, etc.).
function OperationSelectionDropdown({
  operations,
  currentOperation,
  setOperationSelection,
}: OperationSelectionDropdownProps) {
  const [openMenu, setOpenMenu] = useState(false);

  return (
    <div style={{ position: "relative", marginLeft: "auto" }}>
      <DropdownMenuButton
        open={openMenu}
        handleClick={() => {
          setOpenMenu(!openMenu);
        }}
      >
        <Button btnSize="medium" btnStyle="primary">
          <IconMiniChevronDown size={12} />
          <span>Operations</span>
        </Button>
      </DropdownMenuButton>
      {openMenu && (
        <DropdownMenu onClose={() => setOpenMenu(false)}>
          {operations.map((operation) => (
            <Link
              className={styles.dropdownItem}
              key={operation}
              onClick={() => {
                setOperationSelection(operation);
                setOpenMenu(false);
              }}
            >
              <span className={cx({ styles, selected: operation === currentOperation })}>{operation}</span>{" "}
              {operation === currentOperation && <IconCheck />}
            </Link>
          ))}
        </DropdownMenu>
      )}
    </div>
  );
}

export const Route = createFileRoute("/admin/plan-analysis")({
  component: withAuthentication(AdminPlanAnalysisPage, { superAdmin: true }),
});
